/**
 * Login page. Username-or-email + password.
 *
 * On success → navigate to `?next=` if present (and same-origin), else `/`.
 *
 * Public route — does NOT use AppLayout (which assumes the user is logged
 * in and renders the tab bar). Renders a centered card on the same body
 * background.
 */

import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";

function safeNext(search: string): string {
  const params = new URLSearchParams(search);
  const next = params.get("next");
  if (!next) return "/";
  // Only allow same-origin paths.
  if (!next.startsWith("/") || next.startsWith("//")) return "/";
  return next;
}

export default function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [loginValue, setLoginValue] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (isAuthenticated) {
    return <Navigate to={safeNext(location.search)} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ login: loginValue.trim(), password });
      navigate(safeNext(location.search), { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setError("Wrong username/email or password.");
        } else {
          setError(err.detail ?? err.message);
        }
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-shell">
      <section className="auth-card">
        <h1 className="auth-card__title">wasschluckter</h1>
        <p className="auth-card__subtitle">Sign in to continue.</p>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="field">
            <label className="field__label" htmlFor="login-id">
              Username or email
            </label>
            <input
              id="login-id"
              className="field__input"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              required
              value={loginValue}
              onChange={(e) => setLoginValue(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="login-pw">
              Password
            </label>
            <input
              id="login-pw"
              className="field__input"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error && (
            <p className="form-status" data-variant="error" role="alert">
              {error}
            </p>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className="button button--primary"
              disabled={submitting || !loginValue || !password}
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}
